import { authedFetch } from '../lib/authedFetch';
import type { CadImportPayload } from '../types/cad';

export const CAD_IMPORT_MAX_BYTES = 20 * 1024 * 1024;

interface CadErrorBody {
  error?: string;
  code?: string;
}

function isCadImportPayload(value: unknown): value is CadImportPayload {
  if (!value || typeof value !== 'object') return false;
  const payload = value as Partial<CadImportPayload>;
  return typeof payload.fileName === 'string'
    && typeof payload.sourceSha256 === 'string'
    && Array.isArray(payload.features)
    && Array.isArray(payload.cadLayers)
    && Array.isArray(payload.warnings)
    && payload.bounds != null;
}

export async function importDwgFile(file: File, signal?: AbortSignal): Promise<CadImportPayload> {
  if (!file.name.toLowerCase().endsWith('.dwg')) throw new Error('Choose an AutoCAD .dwg file.');
  if (file.size <= 0) throw new Error('The DWG file is empty.');
  if (file.size > CAD_IMPORT_MAX_BYTES) throw new Error('DWG files must be 20 MB or smaller.');

  const response = await authedFetch('/api/cad/import', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/vnd.dwg',
      'X-Cad-File-Name': encodeURIComponent(file.name),
    },
    body: file,
    signal,
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({})) as CadErrorBody;
    throw new Error(body.error || `CAD import failed (${response.status}).`);
  }
  const payload = await response.json() as unknown;
  if (!isCadImportPayload(payload)) throw new Error('CAD converter returned an invalid response.');
  return payload;
}
